import {
  Button,
  CardMedia,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  IconButton,
  MenuItem,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Checkbox,
  FormControlLabel,
  Tooltip,
  DialogActions,
  Grid,
  Autocomplete,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
  InputAdornment,
  Box,
} from '@mui/material';
import {
  DAYS_OF_WEEK,
  PRODUCTS_TAG_SUGGESTIONS,
  ITEM_TYPES,
  STORAGE_KEYS,
  ROW_MATERIAL_STOCK_UPDATE_OPTION,
} from 'const';
import React, { useContext, useState, useRef, useEffect, useCallback, useMemo } from 'react';
import styles from './styles.module.css';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import BarcodeHelper from 'helpers/BarcodeHelper';
import useBarcodeScanner from 'hooks/useBarcodeScanner';
import ItemService from 'services/ItemService';
import PriceCategoryService from 'services/PriceCategoryService';
import ItemAddonService from 'services/ItemAddonService';
import VariantService from 'services/VariantService';
import ShopService from 'services/ShopService';
import ShopsHelper from 'helpers/ShopsHelper';
import { AlertMessagesContext } from 'react-alert-messages';
import { InventoryContext } from 'Context/InventoryContext';
import { ShopContext } from 'Context/ShopContext';
import DescriptionField from 'components/utils/CustomComponents/DescriptionField';
import NumberField from 'components/library/NumberField';
import SelectShopsDialog from './SelectShopsDialog';
import CreateVariantMappingDialog from './CreateVariantMappingDialog';
import ConfirmPopup from 'components/utils/Alert/ConfirmPopup';
import AddCategoryDialog from './AddCategoryDialog';
import { useTranslation } from 'react-i18next';
import { TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import AssistantIcon from '@mui/icons-material/Assistant';
import AIImageSuggestionDialog from './AIImageSuggestionDialog';

const DELTA_TYPE = {
  MORE: 'more',
  LESS: 'less',
};

export default function ItemDialog(props) {
  const { handleClose, item = null, itemCount = 0} = props;
  const isEdit = !!item?.id;
  const { t: tCommon } = useTranslation('common');
  const { t: tPopup } = useTranslation('popup');

  const { postAlertMessage } = useContext(AlertMessagesContext);
  const {
    products: items,
    categories,
    priceCategories,
    chargeGroups,
    getCategories,
    getItemPriceCategories,
    getItemAddonMappings,
    getProductVariantMapping,
    getVariantLabels,
    getVariantDimensions,
    itemAddonMappings,
    getProducts,
    getChargeGroups,
    getPriceCategories,
    itemPriceCategories: contextItemPriceCategories,
    productVariantMapping: contextProductVariantMapping,
    setProducts,
    setItemPriceCategories: setContextPriceCategories,
    setItemAddonMappings: setContextAddonMappings,
    setProductVariantMapping,
    setVariantLabels,
    setVariantDimensions,
    getBaseProducts,
    baseProducts: contextBaseProducts,
    getStockBatches,
  } = useContext(InventoryContext);
  const { shop } = useContext(ShopContext);

  const unitOptions = useMemo(() => {
    const customUnits = shop?.units
      ? shop.units
          .split(',')
          .map((u) => u.trim())
          .filter(Boolean)
      : [];
    const defaultUnits = [
      'Pcs',
      'Kg',
      'Gm',
      'Ltr',
      'Ml',
      'Box',
      'Pack',
      'Pair',
      'Set',
      'Bottle',
      'Can',
      'Dozen',
      'Nos',
      'Unit',
      'Mtr',
    ];
    return Array.from(new Set([...customUnits, ...defaultUnits]));
  }, [shop?.units]);

  const initialName = props.initialName;
  const initialBarcode = props.initialBarcode || props.initialEan;
  const isBarcode = Boolean(initialBarcode || (initialName && (/^\d{3,}$/.test(initialName.trim()) || (/^[A-Za-z0-9_-]{4,}$/.test(initialName.trim()) && /\d/.test(initialName.trim())))));
  const prefilledBarcode = initialBarcode || (isBarcode ? initialName.trim() : '');
  const prefilledName = isBarcode && !initialBarcode ? '' : (initialName || '');

  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState({
    plu: item?.plu || itemCount + 1,
    name: item?.name || prefilledName,
    price: item?.price ?? '',
    mrp: item?.mrp || '',
    cost: item?.cost || '',
    unit: item?.unit || '',
    category: item?.category || '',
    type: item?.type || (props.initialType || ITEM_TYPES.SERVICE),
    is_available: item ? !!item.is_available : true,
    sync_enabled: item ? !!item.sync_enabled : true,
    is_available_on_cart: item ? !!item.is_available_on_cart : false,
    stock: item?.stock || 0,
    hsn: item?.hsn ?? 0,
    ean: item?.ean || prefilledBarcode,
    order: item?.order || 0,
    kg_ratio: item?.kg_ratio || null,
    kg_price: item?.price && item?.kg_ratio ? (Number(item.price) * Number(item.kg_ratio)).toString() : '',
    tax: item?.tax ?? 0,
    profit_margin: item?.config?.profit_margin ?? '',
    cess: item?.cess ?? 0,
    external_item_id: item?.external_item_id || '',
    internal_name: item?.internal_name || '',
    description: item?.description || '',
    charge_groups: item?.charge_groups
      ? Array.isArray(item.charge_groups)
        ? item.charge_groups
        : typeof item.charge_groups === 'string'
        ? item.charge_groups.split(',').map((id) => Number(id.trim()))
        : []
      : [],
    least_price: item?.least_price || '',
    stock_on_hold: item?.stock_on_hold || 0,
    is_batch_enabled: item ? !!item.is_batch_enabled : false,
    shelf_duration_in_hours: item?.shelf_duration_in_hours || '',
    expiry_alert_duration_in_hours: item?.expiry_alert_duration_in_hours || '',
    expected_sold_out_duration_in_hours: item?.expected_sold_out_duration_in_hours || '',
    low_stock_alert_quantity: item?.low_stock_alert_quantity || '',
  });

  const [itemAdvanced, setItemAdvanced] = useState(() => {
    const details = item?.details || {};
    if (!isEdit) {
      return {
        ...details,
        row_material_stock_update_option: ROW_MATERIAL_STOCK_UPDATE_OPTION.CONSUMPTION,
      };
    }
    return {
      ...details,
      row_material_stock_update_option:
        details.row_material_stock_update_option || ROW_MATERIAL_STOCK_UPDATE_OPTION.LIVE,
    };
  });
  const [imageData, setImageData] = useState({});
  const [otherNames, setOtherNames] = useState(
    item?.config?.other_names
      ? Object.entries(item.config.other_names).map(([k, v]) => ({ name: k, option: v }))
      : [{ name: '', option: '' }]
  );
  const [selectedDays, setSelectedDays] = useState([]);
  const [timeRanges, setTimeRanges] = useState([{ from: null, to: null }]);
  const [itemPriceCategories, setItemPriceCategories] = useState([]);
  const [initialItemPriceCategories, setInitialItemPriceCategories] = useState([]);
  const [selectedPriceCategoryItem, setSelectedPriceCategoryItem] = useState({});
  const [variants, setVariants] = useState([]);
  const [selectedDimension, setSelectedDimension] = useState(null);

  const [shelfDurationUnit, setShelfDurationUnit] = useState('hours');
  const [expiryAlertUnit, setExpiryAlertUnit] = useState('hours');
  const [expectedSoldOutUnit, setExpectedSoldOutUnit] = useState('hours');

  const productVariantDimensions = useMemo(() => {
    if (!variants || variants.length === 0) return [];
    const grouped = variants.reduce((acc, curr) => {
      const dim = curr.dimension;
      if (dim?.id && !acc.some((d) => String(d.id) === String(dim.id))) {
        acc.push(dim);
      }
      return acc;
    }, []);
    return grouped;
  }, [variants]);

  const duplicateBarcodeItem = useMemo(() => {
    return BarcodeHelper.findDuplicateBarcodeItem(data.ean, items, item?.id);
  }, [data.ean, items, item?.id]);

  useBarcodeScanner({
    isCapture: true,
    minBufferLength: 2,
    onScan: (scannedCode) => {
      setShowAdvanced(true);
      setData((prev) => {
        const updated = { ...prev, ean: scannedCode };
        ['name', 'price', 'cost', 'mrp', 'least_price', 'external_item_id', 'internal_name', 'kg_ratio', 'kg_price', 'profit_margin', 'description'].forEach((field) => {
          const val = updated[field];
          if (val !== undefined && val !== null && val !== '') {
            const strVal = String(val);
            if (strVal === scannedCode || strVal === String(scannedCode).slice(0, 1)) {
              updated[field] = '';
            } else if (strVal.endsWith(scannedCode)) {
              updated[field] = strVal.slice(0, -scannedCode.length).trim();
            }
          }
        });
        return updated;
      });
      const existing = BarcodeHelper.findDuplicateBarcodeItem(scannedCode, items, item?.id);
      if (existing) {
        postAlertMessage({
          text: `Warning: Barcode "${scannedCode}" already exists for product: "${existing.name}" (PLU: ${existing.plu})`,
          type: 'warning',
        });
      }
    },
    deps: [item?.id, items, postAlertMessage],
  });

  const selectedDimensionLabels = useMemo(() => {
    if (!selectedDimension || !variants) return [];
    return variants
      .filter((v) => String(v.dimension?.id) === String(selectedDimension))
      .map((v) => v.label)
      .filter(Boolean);
  }, [selectedDimension, variants]);

  const [itemAddons, setItemAddons] = useState([]);
  const [initialItemAddons, setInitialItemAddons] = useState([]);
  const [selectedAddon, setSelectedAddon] = useState({});
  const [addonDeltaType, setAddonDeltaType] = useState(DELTA_TYPE.MORE);
  const [tags, setTags] = useState(
    item?.config?.tags
      ? Object.entries(item.config.tags).map(([k, v]) => ({ key: k, value: v }))
      : [{ key: '', value: '' }]
  );
  const [isShowSelectShopsDialog, setIsShowSelectShopsDialog] = useState(false);
  const [variantMappingData, setVariantMappingData] = useState(null);
  const [dltProductVariantMapping, setDltProductVariantMapping] = useState(null);
  const [subItems, setSubItems] = useState([]);
  const [selectedProductOption, setSelectedProductOption] = useState({ quantity_ratio: 1 });
  const [isShowAddCategoryDialog, setIsShowAddCategoryDialog] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(
    isEdit || Boolean(item?.ean || prefilledBarcode)
  );

  useEffect(() => {
    if (data.ean && String(data.ean).trim() !== '' && String(data.ean).trim() !== 'nil') {
      setShowAdvanced(true);
    }
  }, [data.ean]);
  const [productOptions, setProductOptions] = useState(item?.config?.product_options || []);
  const [selectedProdOption, setSelectedProdOption] = useState({ quantity_ratio: 1 });
  const [isAIImageSuggestionDialogOpen, setIsAIImageSuggestionDialogOpen] = useState(false);
  const [activeAIImageField, setActiveAIImageField] = useState(null);

  const priceCategorySectionRef = useRef(null);
  const addonSectionRef = useRef(null);

  const config = item?.config || {};

  const calculateCostFromSubItems = useCallback(
    (sItems) => {
      if (!sItems || sItems.length === 0) return 0;
      let totalCost = 0;
      (sItems || []).forEach((si) => {
        const product = (items || []).find((i) => i.id === (si.sub_item_id || si.id));
        if (product && product.cost) {
          const itemCost = Number(product.cost) || 0;
          const ratio = Number(si.quantity_ratio) || 0;
          totalCost += itemCost * ratio;
        }
      });
      return Math.round(totalCost * 100) / 100;
    },
    [items]
  );

  useEffect(() => {
    if (data.type === ITEM_TYPES.COMBO || data.type === ITEM_TYPES.PRODUCTION) {
      const calculatedCost = calculateCostFromSubItems(subItems);
      if (calculatedCost > 0 && calculatedCost !== Number(data.cost)) {
        setData((prev) => ({ ...prev, cost: calculatedCost }));
      }
    }
  }, [subItems, data.type, calculateCostFromSubItems, data.cost]);

  useEffect(() => {
    if (!isEdit && itemCount) {
      setData((prev) => ({ ...prev, plu: itemCount + 1 }));
    }
  }, [itemCount, isEdit]);

  useEffect(() => {
    getProducts();
    getCategories();
    getChargeGroups();
    getPriceCategories();
    getItemPriceCategories();
    getItemAddonMappings();
    getProductVariantMapping();
    getVariantLabels();
    getVariantDimensions();
    getBaseProducts();
  }, [
    getProducts,
    getCategories,
    getChargeGroups,
    getPriceCategories,
    getItemPriceCategories,
    getItemAddonMappings,
    getProductVariantMapping,
    getVariantLabels,
    getVariantDimensions,
    getBaseProducts,
  ]);

  useEffect(() => {
    if (!isEdit || !item) return;
    const additions = (itemAddonMappings || [])
      .filter((a) => a.item === item.id)
      .map((a) => {
        const addonItem = (items || []).find((i) => i.id === a.addon_item);
        return {
          ...a,
          addon_item_id: a.addon_item,
          itemName: addonItem ? addonItem.name : '',
        };
      });
    setItemAddons(additions);
    setInitialItemAddons(additions);
  }, [item?.id, items, item, itemAddonMappings, isEdit]);

  useEffect(() => {
    if (!isEdit || !item) return;
    const filteredPC = (contextItemPriceCategories || []).filter((p) => p.item === item.id);
    setItemPriceCategories(filteredPC);
    setInitialItemPriceCategories(filteredPC);
  }, [item?.id, contextItemPriceCategories, isEdit, item]);

  useEffect(() => {
    if (!item) return;
    setSelectedDays(
      item.available_days
        ? item.available_days
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : []
    );
    if (item.available_time_range) {
      const ranges = item.available_time_range
        .split(',')
        .map((str) => str.trim())
        .filter(Boolean)
        .map((part) => {
          const [fromStr, toStr] = part.split('-');
          const parseTime = (hhmm) => {
            if (!hhmm) return null;
            const [hh, mm] = hhmm.split(':').map(Number);
            if (isNaN(hh) || isNaN(mm)) return null;
            const d = new Date();
            d.setHours(hh, mm, 0, 0);
            return d;
          };
          const from = parseTime(fromStr),
            to = parseTime(toStr);
          return from && to ? { from, to } : null;
        })
        .filter((r) => r);
      setTimeRanges(ranges.length ? ranges : [{ from: null, to: null }]);
    } else {
      setTimeRanges([{ from: null, to: null }]);
    }

    if (item?.shelf_duration_in_hours && Number(item.shelf_duration_in_hours) >= 24) {
      setShelfDurationUnit('days');
    } else {
      setShelfDurationUnit('hours');
    }
    if (item?.expiry_alert_duration_in_hours && Number(item.expiry_alert_duration_in_hours) >= 24) {
      setExpiryAlertUnit('days');
    } else {
      setExpiryAlertUnit('hours');
    }
    if (item?.expected_sold_out_duration_in_hours && Number(item.expected_sold_out_duration_in_hours) >= 24) {
      setExpectedSoldOutUnit('days');
    } else {
      setExpectedSoldOutUnit('hours');
    }
  }, [item]);

  const fetchVariantMapping = useCallback(
    async (forceRefresh = false) => {
      if (!isEdit) return;
      try {
        const [mapping, labels, dims] = await Promise.all([
          getProductVariantMapping({ useCache: !forceRefresh }),
          getVariantLabels({ useCache: !forceRefresh }),
          getVariantDimensions({ useCache: !forceRefresh }),
        ]);

        setProductVariantMapping(mapping);
        setVariantLabels(labels);
        setVariantDimensions(dims);

        const mappingsForItem = mapping.filter((m) => String(m.item) === String(item.id));
        const v = mappingsForItem.map((m) => {
          const label = labels.find((l) => String(l.id) === String(m.label));
          const dimension =
            label?.dimension && typeof label.dimension === 'object'
              ? label.dimension
              : dims.find((d) => String(d.id) === String(label?.dimension));
          return {
            ...m,
            label: label,
            dimension: dimension,
            baseProduct: (contextBaseProducts || []).find((bp) => String(bp.id) === String(m.product)),
          };
        });
        setVariants(v);
      } catch (error) {
        console.error('Failed to fetch item variants', error);
      }
    },
    [
      isEdit,
      item?.id,
      getProductVariantMapping,
      getVariantLabels,
      getVariantDimensions,
      setProductVariantMapping,
      setVariantLabels,
      setVariantDimensions,
      contextBaseProducts,
    ]
  );

  useEffect(() => {
    if (isEdit) {
      const fetchEditData = async () => {
        try {
          fetchVariantMapping();

          if (item.type === ITEM_TYPES.COMBO || item.type === ITEM_TYPES.PRODUCTION) {
            const rawSubItems = item.details?.sub_items || item.product_sub_items || item.sub_items || [];
            const si = rawSubItems.map((s) => {
              const subId = s.item_id || s.sub_item || s.sub_item_id || s.id;
              const matchedItem = (items || []).find((i) => i.id === subId);
              return {
                ...s,
                sub_item_id: subId,
                sub_item_name: matchedItem?.name || s.sub_item_name || s.name || '',
                quantity_ratio: s.fraction || s.quantity_ratio || 1,
              };
            });
            setSubItems(si);
          }
        } catch (error) {
          console.error('Failed to fetch item details', error);
        }
      };
      fetchEditData();
    }
  }, [
    isEdit,
    item?.id,
    item?.product_sub_items,
    item?.details?.sub_items,
    item?.sub_items,
    item?.type,
    getItemPriceCategories,
    getItemAddonMappings,
    fetchVariantMapping,
    items,
  ]);

  useEffect(() => {
    if (!selectedDimension && productVariantDimensions.length > 0) {
      setSelectedDimension(productVariantDimensions[0].id);
    }
  }, [productVariantDimensions, selectedDimension]);

  const openAIImageSuggestion = (field) => {
    setActiveAIImageField(field);
    setIsAIImageSuggestionDialogOpen(true);
  };

  const handleAIImageSelect = async (imageUrl) => {
    setIsAIImageSuggestionDialogOpen(false);
    setIsLoading(true);
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageData((prev) => ({
          ...prev,
          [activeAIImageField]: reader.result,
        }));
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      console.error('Failed to process AI image selection:', error);
      postAlertMessage({
        text: 'Failed to select AI image. Please try again.',
        type: 'failed',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getFullRequestData = () => {
    const req = {
      name: data.name,
      unit: data.unit,
      price: data.price,
      mrp: data.mrp || data.price,
      cost: data.cost || 0,
      tax: data.tax || 0,
      cess: data.cess || 0,
      stock: data.stock || 0,
      category: data.category || null,
      type: data.type,
      hsn: data.hsn || 0,
      ean: data.ean || 'nil',
      order: data.order === '' ? null : data.order,
      is_available: data.is_available,
      sync_enabled: data.sync_enabled,
      is_available_on_cart: data.is_available_on_cart,
      kg_ratio: data.kg_ratio,
      external_item_id: data.external_item_id || null,
      internal_name: data.internal_name || null,
      description: data.description || null,
      charge_groups: data.charge_groups,
      least_price: data.least_price || null,
      stock_on_hold: data.stock_on_hold || 0,
      is_batch_enabled: data.is_batch_enabled,
      shelf_duration_in_hours: data.shelf_duration_in_hours || null,
      expiry_alert_duration_in_hours: data.expiry_alert_duration_in_hours || null,
      expected_sold_out_duration_in_hours: data.expected_sold_out_duration_in_hours || null,
      low_stock_alert_quantity: data.low_stock_alert_quantity || null,
    };

    if (isEdit) {
      req.plu = data.plu;
    }

    req.details = {
      ...itemAdvanced,
      row_material_stock_update_option:
        data.type === ITEM_TYPES.COMBO
          ? itemAdvanced.row_material_stock_update_option ||
            (!isEdit ? ROW_MATERIAL_STOCK_UPDATE_OPTION.CONSUMPTION : ROW_MATERIAL_STOCK_UPDATE_OPTION.LIVE)
          : ROW_MATERIAL_STOCK_UPDATE_OPTION.LIVE,
      sub_items: subItems.map((si) => ({
        item_id: si.sub_item_id || si.id,
        fraction: si.quantity_ratio,
      })),
    };

    const newTagsObj = {};
    tags.forEach(({ key, value }) => {
      const k = (key ?? '').trim();
      if (!k) return;
      const v = value ?? '';
      if (Object.prototype.hasOwnProperty.call(newTagsObj, k)) {
        if (Array.isArray(newTagsObj[k])) newTagsObj[k].push(v);
        else newTagsObj[k] = [newTagsObj[k], v];
      } else {
        newTagsObj[k] = v;
      }
    });

    req.config = {
      ...(config || {}),
      tags: newTagsObj,
      product_options: productOptions,
      profit_margin: data.profit_margin === '' ? 0 : Number(data.profit_margin),
    };

    req.available_days = selectedDays.length ? selectedDays.join(',') : '';

    const serializeRanges = () => {
      return timeRanges
        .map(({ from, to }) => {
          if (!from || !to) return null;
          const pad = (n) => String(n).padStart(2, '0');
          const fH = pad(from.getHours()),
            fM = pad(from.getMinutes());
          const tH = pad(to.getHours()),
            tM = pad(to.getMinutes());
          return `${fH}:${fM}-${tH}:${tM}`;
        })
        .filter(Boolean)
        .join(',');
    };
    req.available_time_range = serializeRanges();

    req.other_names = otherNames
      .filter((item) => item.name.trim())
      .map((item) => (item.option.trim() ? `${item.name.trim()} | Option: ${item.option.trim()}` : item.name.trim()))
      .join('\n');

    return req;
  };

  const NUMERIC_FIELDS = [
    'price',
    'mrp',
    'cost',
    'profit_margin',
    'tax',
    'cess',
    'stock',
    'plu',
    'order',
    'kg_ratio',
    'least_price',
    'stock_on_hold',
    'hsn',
    'shelf_duration_in_hours',
    'expiry_alert_duration_in_hours',
    'expected_sold_out_duration_in_hours',
    'low_stock_alert_quantity',
  ];

  const getRequestDataToUpdate = () => {
    if (!isEdit) return {};
    const requestData = {};
    const req = getFullRequestData();

    Object.keys(req).forEach((key) => {
      let currentValue = req[key];
      let originalValue = key === 'charge_group_id' ? item['charge_group'] : item[key];

      if (key === 'config') {
        const normalizedItemConfig = (() => {
          try {
            return typeof originalValue === 'string' && originalValue.trim()
              ? JSON.parse(originalValue)
              : originalValue || {};
          } catch (e) {
            return {};
          }
        })();
        if (JSON.stringify(currentValue) !== JSON.stringify(normalizedItemConfig)) {
          requestData[key] = currentValue;
        }
        return;
      }

      if (key === 'details') {
        if (JSON.stringify(currentValue) !== JSON.stringify(originalValue || {})) {
          requestData[key] = currentValue;
        }
        return;
      }

      if (NUMERIC_FIELDS.includes(key)) {
        currentValue = currentValue === null || currentValue === '' ? null : Number(currentValue);
        originalValue =
          originalValue === null || originalValue === undefined || originalValue === '' ? null : Number(originalValue);
      } else {
        if (currentValue === undefined) currentValue = null;
        if (originalValue === undefined) originalValue = null;
      }

      if (currentValue !== originalValue) {
        requestData[key] = currentValue;
      }
    });

    return requestData;
  };

  const handleSaveItem = async (shopIds) => {
    if (duplicateBarcodeItem) {
      postAlertMessage({
        text: `Barcode "${data.ean.trim()}" already exists for product: "${duplicateBarcodeItem.name}" (PLU: ${duplicateBarcodeItem.plu}). Please use a unique barcode.`,
        type: 'failed',
      });
      return;
    }

    if (data.name === '' || data.price === '' || data.unit === '' || data.category === '') {
      postAlertMessage({ text: tCommon('Please fill all required fields.'), type: 'failed' });
      return;
    }

    setIsLoading(true);
    try {
      let resp;
      let requestData = getFullRequestData();

      if (isEdit) {
        requestData = getRequestDataToUpdate();
        if (Object.keys(requestData).length > 0) {
          if (shopIds && shopIds.length > 0) {
            requestData.shop_ids = shopIds;
          }
          resp = await ItemService.updateItem(item.id, requestData);
        } else {
          resp = item;
        }

        if (JSON.stringify(itemAddons) !== JSON.stringify(initialItemAddons)) {
          const respAddons = await ItemAddonService.updateItemAddonMappings(item.id, itemAddons);
          setContextAddonMappings((prev) => {
            const filtered = prev.filter((m) => m.item !== item.id);
            return [...filtered, ...respAddons];
          });
          setInitialItemAddons([...itemAddons]);
        }

        if (JSON.stringify(itemPriceCategories) !== JSON.stringify(initialItemPriceCategories)) {
          const cleanedPC = itemPriceCategories.map((pc) => {
            const catId = pc.category?.id || pc.category_id;
            const { category, ...rest } = pc;
            const updatedPC = { ...rest, category_id: catId };

            if (!initialItemPriceCategories.find((orig) => orig.id === pc.id)) {
              const { id, ...newPC } = updatedPC;
              return newPC;
            }
            return updatedPC;
          });
          const respPC = await PriceCategoryService.updateItemPriceCategories(item.id, cleanedPC);
          setContextPriceCategories((prev) => {
            const filtered = prev.filter((ipc) => ipc.item !== item.id);
            return [...filtered, ...respPC];
          });
          setInitialItemPriceCategories([...itemPriceCategories]);
        }

        let updatedItem = resp;
        if (Object.keys(imageData).length > 0) {
          const imageResp = await ItemService.uploadItemImage(imageData, item.id);
          if (imageResp) {
            updatedItem = imageResp;
          }
        }

        if (updatedItem) {
          setProducts((prev) => prev.map((i) => (i.id === updatedItem.id ? updatedItem : i)));
        }

        postAlertMessage({ text: tCommon('Item updated successfully'), type: 'success' });
        resp = updatedItem;
      } else {
        if (shopIds && shopIds.length > 0) {
          requestData.shop_ids = shopIds;
        }
        resp = await ItemService.addItem(requestData);
        if (resp) {
          if (itemAddons.length > 0) {
            const respAddons = await ItemAddonService.updateItemAddonMappings(resp.id, itemAddons);
            setContextAddonMappings((prev) => [...prev, ...respAddons]);
          }
          if (itemPriceCategories.length > 0) {
            const cleanedPC = itemPriceCategories.map((pc) => {
              const catId = pc.category?.id || pc.category_id;
              const { id, category, ...rest } = pc;
              return { ...rest, category_id: catId };
            });
            const respPC = await PriceCategoryService.updateItemPriceCategories(resp.id, cleanedPC);
            setContextPriceCategories((prev) => [...prev, ...respPC]);
          }

          let createdItem = {
            ...requestData,
            ...resp,
            ean:
              data.ean && String(data.ean).trim() !== '' && String(data.ean).trim() !== 'nil'
                ? String(data.ean).trim()
                : resp?.ean && String(resp.ean).trim() !== 'nil'
                ? String(resp.ean).trim()
                : null,
            type: resp?.type || requestData.type || ITEM_TYPES.SERVICE,
          };
          if (Object.keys(imageData).length > 0) {
            const imageResp = await ItemService.uploadItemImage(imageData, resp.id);
            if (imageResp) {
              createdItem = { ...createdItem, ...imageResp };
            }
          }

          setProducts((prev) => [createdItem, ...(prev || []).filter((p) => p.id !== createdItem.id)]);
          postAlertMessage({ text: tCommon('Item created successfully'), type: 'success' });
          resp = createdItem;
        }
      }

      if (resp?.is_batch_enabled) {
        await getStockBatches({ useCache: false });
      }

      handleClose(resp);
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    } finally {
      setIsLoading(false);
    }
  };

  const shouldShowSelectOtherShopsDialog = async () => {
    if (isEdit && Object.keys(getRequestDataToUpdate()).length === 0) {
      return false;
    }

    if (!ShopsHelper.hasProAccess(shop)) {
      return false;
    }

    const localPreference = localStorage.getItem(STORAGE_KEYS.DONT_SHOW_OTHER_SHOPS_ON_ITEM_EDIT);
    if (localPreference && localPreference === String(true)) {
      return false;
    }

    setIsLoading(true);
    const shops = await ShopService.getShops();
    setIsLoading(false);
    return shops.length > 1;
  };

  const onSaveItemButtonClicked = async () => {
    if (data.name === '' || data.price === '' || data.unit === '' || data.category === '') {
      postAlertMessage({ text: tCommon('Please fill all required fields.'), type: 'failed' });
      return;
    }

    if ((data.type === ITEM_TYPES.COMBO || data.type === ITEM_TYPES.PRODUCTION) && selectedProductOption?.sub_item_id) {
      postAlertMessage({ text: tPopup('Please add selected sub-item before saving.'), type: 'failed' });
      return;
    }

    if (await shouldShowSelectOtherShopsDialog()) {
      setIsShowSelectShopsDialog(true);
      return;
    }
    await handleSaveItem();
  };

  const onOtherShopsSelected = async (shops) => {
    setIsShowSelectShopsDialog(false);
    await handleSaveItem(shops);
  };

  const handleAddOtherNameRow = () => {
    setOtherNames([...otherNames, { name: '', option: '' }]);
  };

  const handleRemoveOtherNameRow = (index) => {
    const updated = otherNames.filter((_, i) => i !== index);
    setOtherNames(updated.length ? updated : [{ name: '', option: '' }]);
  };

  const handleOtherNameChange = (index, field, value) => {
    const updated = [...otherNames];
    updated[index][field] = value;
    setOtherNames(updated);
  };

  const handleDayChange = (event, newDays) => setSelectedDays(newDays);

  const handleTimeRangeChange = (index, field, value) => {
    setTimeRanges((prev) => {
      const arr = [...prev];
      arr[index] = { ...arr[index], [field]: value };
      return arr;
    });
  };

  const addRangeRow = () => {
    setTimeRanges((prev) => [...prev, { from: null, to: null }]);
  };

  const removeRangeRow = (index) => {
    setTimeRanges((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddCategoryItem = (pcItem) => {
    const newPC = {
      ...pcItem,
      id: pcItem.id || Date.now(),
    };
    setItemPriceCategories((prev) => [...prev, newPC]);
    setSelectedPriceCategoryItem({});
  };

  const handleDltCategoryItem = (pcItem) => {
    setItemPriceCategories((prev) => prev.filter((i) => i.id !== pcItem.id));
  };

  const handleEditCategoryItem = (pcItem) => {
    setSelectedPriceCategoryItem({
      ...pcItem,
      category_id: pcItem.category_id || pcItem.category?.id,
    });
    handleDltCategoryItem(pcItem);
    priceCategorySectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleAddAddon = (addon) => {
    const price_delta = addonDeltaType === DELTA_TYPE.LESS ? -Math.abs(addon.price_delta) : Math.abs(addon.price_delta);
    const newAddon = {
      ...addon,
      price_delta,
      itemName: addon.itemName,
      is_available: addon.is_available !== false,
    };
    if (addon.id) {
      newAddon.id = addon.id;
    } else {
      delete newAddon.id;
    }
    setItemAddons((prev) => [...prev, newAddon]);
    setSelectedAddon({});
    setAddonDeltaType(DELTA_TYPE.MORE);
  };

  const handleDltAddon = (addon) => {
    setItemAddons((prev) =>
      prev.filter((i) => i.addon_item_id !== addon.addon_item_id || i.price_delta !== addon.price_delta)
    );
  };

  const handleEditAddon = (addon) => {
    setSelectedAddon(addon);
    setAddonDeltaType(addon.price_delta < 0 ? DELTA_TYPE.LESS : DELTA_TYPE.MORE);
    handleDltAddon(addon);
    addonSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleAddSubItem = (opt) => {
    setSubItems([
      ...subItems,
      {
        ...opt,
        quantity_ratio: opt.quantity_ratio === '' || opt.quantity_ratio === undefined ? 1 : Number(opt.quantity_ratio),
      },
    ]);
    setSelectedProductOption({ quantity_ratio: 1 });
  };

  const handleDltSubItem = (opt) => {
    setSubItems(subItems.filter((i) => (i.id && opt.id ? i.id !== opt.id : i.sub_item_id !== opt.sub_item_id)));
  };

  const handleAddProdOption = (option) => {
    const finalOption = {
      ...option,
      quantity_ratio:
        option.quantity_ratio === '' || option.quantity_ratio === undefined ? 1 : Number(option.quantity_ratio),
    };
    setProductOptions((prev) => [...prev, finalOption]);
    setSelectedProdOption({ quantity_ratio: 1 });
  };

  const handleDltProdOption = (option) => {
    setProductOptions((prev) =>
      prev.filter(
        (i) => i.label !== option.label || i.price !== option.price || i.quantity_ratio !== option.quantity_ratio
      )
    );
  };

  const handleEditProdOption = (option) => {
    setSelectedProdOption(option);
    handleDltProdOption(option);
  };

  const handleDeleteProductVariantMapping = async () => {
    setIsLoading(true);
    try {
      await VariantService.deleteProductVariantMapping(dltProductVariantMapping.id);
      setVariants(variants.filter((v) => v.id !== dltProductVariantMapping.id));
      postAlertMessage({ text: tCommon('Variant deleted'), type: 'success' });
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    }
    setDltProductVariantMapping(null);
    setIsLoading(false);
  };

  const groupedPriceCategoryItems = useMemo(() => {
    const grouped = (itemPriceCategories || []).reduce((acc, curr) => {
      const catId = curr.category?.id || curr.category_id;
      if (!acc[catId]) {
        acc[catId] = {
          category: curr.category || (priceCategories || []).find((c) => c.id === catId),
          items: [],
        };
      }
      acc[catId].items.push(curr);
      return acc;
    }, {});
    return Object.values(grouped).map((g) => ({
      ...g,
      items: g.items.sort((a, b) => (a.minimum_quantity || 0) - (b.minimum_quantity || 0)),
    }));
  }, [itemPriceCategories, priceCategories]);

  const onCreateCategory = async (newCat) => {
    setIsShowAddCategoryDialog(false);
    setIsLoading(true);
    await getCategories({ useCache: false });
    if (newCat) {
      setData((prev) => ({ ...prev, category: newCat.id }));
    }
    setIsLoading(false);
  };

  return (
    <div className={styles.popUp} style={{ zIndex: 1350 }}>
      <div className={styles.fullPageContentWrapper}>
        <div className={styles.headSec}>
          <h2 className={styles.editTitle}>{isEdit ? tCommon('Edit Item') : tCommon('Create Item')}</h2>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </div>

        <div className={styles.inputSec}>
          <Grid container spacing={1}>
            {isEdit && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={tCommon('Item ID')}
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled
                    value={item?.id || ''}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={tCommon('PLU')}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={data.plu}
                    onChange={(e) => setData({ ...data, plu: e.target.value })}
                  />
                </Grid>
              </>
            )}
            {/* Essential Info - Row 1: Name and Category */}
            <Grid item xs={12} sm={6}>
              <TextField
                label={tCommon('Name')}
                variant="outlined"
                size="small"
                fullWidth
                required
                name="name"
                value={data.name}
                onChange={(e) => setData({ ...data, name: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Grid container>
                <Grid item xs={11} sm={11}>
                  <TextField
                    select
                    label={tCommon('Category')}
                    variant="outlined"
                    size="small"
                    fullWidth
                    required
                    value={data.category || ''}
                    onChange={(e) => setData({ ...data, category: e.target.value })}
                    SelectProps={{
                      MenuProps: {
                        getContentAnchorEl: null,
                        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                        transformOrigin: { vertical: 'top', horizontal: 'left' },
                        PaperProps: { style: { maxHeight: 350 } },
                        style: { zIndex: 10001 },
                      },
                    }}
                  >
                    {(categories || []).map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={1} sm={1} display="flex" alignItems="center" justifyContent="flex-end">
                  {!isEdit && (
                    <Tooltip title={tCommon('Add New Category')}>
                      <IconButton onClick={() => setIsShowAddCategoryDialog(true)} color="primary" size="small">
                        <AddCircleIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Grid>
              </Grid>
            </Grid>

            {/* Essential Info - Row 2: Unit and Price */}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label={tCommon('Unit')}
                variant="outlined"
                size="small"
                fullWidth
                required
                value={data.unit}
                onChange={(e) => setData({ ...data, unit: e.target.value })}
                SelectProps={{
                  MenuProps: {
                    getContentAnchorEl: null,
                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                    transformOrigin: { vertical: 'top', horizontal: 'left' },
                    PaperProps: { style: { maxHeight: 350 } },
                    style: { zIndex: 10001 },
                  },
                }}
              >
                {unitOptions.map((u) => (
                  <MenuItem key={u} value={u}>
                    {u}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <NumberField
                label={tCommon('Price')}
                variant="outlined"
                size="small"
                fullWidth
                required
                value={data.price ?? ''}
                onChange={(e) => {
                  const price = e.target.value;
                  setData((prev) => {
                    let kg_price = prev.kg_price;
                    let kg_ratio = prev.kg_ratio;
                    if (price === '') {
                      kg_price = '';
                    } else {
                      const priceVal = parseFloat(price) || 0;
                      if (prev.kg_ratio && priceVal) {
                        kg_price = (priceVal * parseFloat(prev.kg_ratio)).toString();
                      } else {
                        kg_price = price;
                        kg_ratio = 1;
                      }
                    }
                    return { ...prev, price, kg_price, kg_ratio };
                  });
                }}
              />
            </Grid>

            {/* Product Type Box */}
            <Grid item xs={12}>
              <Grid
                container
                style={{
                  backgroundColor: '#f9f9f9',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #e0e0e0',
                }}
              >
                <Grid item xs={12} sm={6} p={0.5}>
                  <TextField
                    select
                    label={tCommon('Type')}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={data.type}
                    SelectProps={{
                      MenuProps: {
                        getContentAnchorEl: null,
                        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                        transformOrigin: { vertical: 'top', horizontal: 'left' },
                        PaperProps: { style: { maxHeight: 350 } },
                        style: { zIndex: 10001 },
                      },
                    }}
                    onChange={(e) => {
                      const newType = e.target.value;
                      const isServiceOrCombo = newType === ITEM_TYPES.SERVICE || newType === ITEM_TYPES.COMBO;
                      setData({
                        ...data,
                        type: newType,
                        ...(isServiceOrCombo
                          ? {
                              is_batch_enabled: false,
                              shelf_duration_in_hours: '',
                              expiry_alert_duration_in_hours: '',
                              expected_sold_out_duration_in_hours: '',
                              low_stock_alert_quantity: '',
                            }
                          : {}),
                      });
                    }}
                  >
                    <MenuItem value={ITEM_TYPES.INVENTORY}>{tCommon('Inventory')}</MenuItem>
                    <MenuItem value={ITEM_TYPES.COMBO}>{tCommon('Combo')}</MenuItem>
                    <MenuItem value={ITEM_TYPES.SERVICE}>{tCommon('Service')}</MenuItem>
                    <MenuItem value={ITEM_TYPES.PRODUCTION}>{tCommon('Production')}</MenuItem>
                  </TextField>
                </Grid>

                {data.type === ITEM_TYPES.COMBO && (
                  <Grid item xs={12} sm={6} p={0.5}>
                    <TextField
                      select
                      label={tCommon('Raw Material Stock Update Option')}
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={
                        itemAdvanced.row_material_stock_update_option ||
                        (!isEdit ? ROW_MATERIAL_STOCK_UPDATE_OPTION.CONSUMPTION : ROW_MATERIAL_STOCK_UPDATE_OPTION.LIVE)
                      }
                      SelectProps={{
                        MenuProps: {
                          getContentAnchorEl: null,
                          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                          transformOrigin: { vertical: 'top', horizontal: 'left' },
                          PaperProps: { style: { maxHeight: 350 } },
                          style: { zIndex: 10001 },
                        },
                      }}
                      onChange={(e) =>
                        setItemAdvanced({
                          ...itemAdvanced,
                          row_material_stock_update_option: e.target.value,
                        })
                      }
                    >
                      <MenuItem value={ROW_MATERIAL_STOCK_UPDATE_OPTION.LIVE}>{tCommon('Live')}</MenuItem>
                      <MenuItem value={ROW_MATERIAL_STOCK_UPDATE_OPTION.CONSUMPTION}>{tCommon('Consumption')}</MenuItem>
                    </TextField>
                  </Grid>
                )}

                {(data.type === ITEM_TYPES.INVENTORY || data.type === ITEM_TYPES.PRODUCTION) && (
                  <Grid item xs={12} sm={6} p={0.5}>
                    <NumberField
                      label={tCommon('Stock')}
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={data.stock}
                      onChange={(e) => setData({ ...data, stock: e.target.value })}
                    />
                  </Grid>
                )}

                {(data.type === ITEM_TYPES.COMBO || data.type === ITEM_TYPES.PRODUCTION) && (
                  <Grid item xs={12}>
                    <h3 className={styles.inputLabel}>
                      {data.type === ITEM_TYPES.COMBO
                        ? tCommon('Product Sub Items')
                        : tCommon('Product Production Items')}
                    </h3>
                    <TableContainer component={Paper} className={styles.tableContainer}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: '55%' }}>{tCommon('Item')}</TableCell>
                            <TableCell sx={{ width: '30%' }}>{tCommon('Fraction')}</TableCell>
                            <TableCell sx={{ width: '15%' }} align="right">
                              {tCommon('Actions')}
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {subItems.map((si, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{si.sub_item_name}</TableCell>
                              <TableCell>{si.quantity_ratio}</TableCell>
                              <TableCell align="right">
                                <IconButton size="small" color="error" onClick={() => handleDltSubItem(si)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow>
                            <TableCell>
                              <Autocomplete
                                openOnFocus
                                disablePortal
                                PopperProps={{ style: { zIndex: 14000 } }}
                                size="small"
                                options={items || []}
                                getOptionLabel={(option) =>
                                  option?.plu ? `${option.plu} - ${option.name || ''}` : option?.name || ''
                                }
                                renderOption={(props, option) => (
                                  <li {...props} key={option.id}>
                                    <Box style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                      <span>{option.name}</span>
                                      <span style={{ color: '#718096', fontSize: '12px' }}>(PLU: {option.plu})</span>
                                    </Box>
                                  </li>
                                )}
                                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                filterOptions={(options, { inputValue }) => {
                                  const search = (inputValue || '').trim().toLowerCase();
                                  if (!search) return options;
                                  return options.filter((option) => {
                                    const name = (option?.name || '').toLowerCase();
                                    const plu = (option?.plu || '').toString().toLowerCase();
                                    const ean = (option?.ean || '').toString().toLowerCase();
                                    return name.includes(search) || plu.includes(search) || ean.includes(search);
                                  });
                                }}
                                value={(items || []).find((i) => i.id === selectedProductOption.sub_item_id) || null}
                                onChange={(e, val) =>
                                  setSelectedProductOption({
                                    ...selectedProductOption,
                                    sub_item_id: val?.id,
                                    sub_item_name: val?.name,
                                  })
                                }
                                renderInput={(params) => (
                                  <TextField {...params} label={tCommon('Search Item')} variant="outlined" />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <NumberField
                                size="small"
                                label={tCommon('Fraction')}
                                value={selectedProductOption.quantity_ratio}
                                onChange={(e) =>
                                  setSelectedProductOption({
                                    ...selectedProductOption,
                                    quantity_ratio: e.target.value,
                                  })
                                }
                              />
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                onClick={() => handleAddSubItem(selectedProductOption)}
                                disabled={!selectedProductOption.sub_item_id}
                              >
                                <AddRoundedIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}
              </Grid>
            </Grid>

            {!isEdit && (
              <Grid item xs={12}>
                <Button
                  fullWidth
                  className={styles.advancedOptionsToggle}
                  endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  onClick={() => setShowAdvanced(!showAdvanced)}
                >
                  {showAdvanced ? 'Hide More Options (Tax, MRP, etc.)' : 'Show More Options (Tax, MRP, etc.)'}
                </Button>
              </Grid>
            )}

            {showAdvanced && (
              <>
                {(!data.charge_groups || (Array.isArray(data.charge_groups) && data.charge_groups.length === 0)) && (
                  <>
                    <Grid item xs={12} sm={4}>
                      <NumberField
                        label={tCommon('Tax(%)')}
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={data.tax}
                        onChange={(e) => setData({ ...data, tax: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <NumberField
                        label={tCommon('Additional Tax(%)')}
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={data.cess}
                        onChange={(e) => setData({ ...data, cess: e.target.value })}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sm={4}>
                  <TextField
                    label={tCommon('HSN')}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={data.hsn}
                    onChange={(e) => setData({ ...data, hsn: e.target.value })}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <NumberField
                    label={tCommon('MRP')}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={data.mrp}
                    onChange={(e) => setData({ ...data, mrp: e.target.value })}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <NumberField
                    label={tCommon('Cost')}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={data.cost}
                    onChange={(e) => {
                      const cost = e.target.value;
                      setData((prev) => ({ ...prev, cost }));
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <NumberField
                    label={tCommon('Least Price (Optional)')}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={data.least_price}
                    onChange={(e) => setData({ ...data, least_price: e.target.value })}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <NumberField
                    label={tCommon('USP Kg Ratio')}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={data.kg_ratio}
                    onChange={(e) => {
                      const kg_ratio = e.target.value;
                      setData((prev) => {
                        const ratio = parseFloat(kg_ratio);
                        const priceVal = parseFloat(prev.price) || 0;
                        const kg_price = ratio && priceVal ? (priceVal * ratio).toString() : '';
                        return { ...prev, kg_ratio, kg_price };
                      });
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <NumberField
                    label={tCommon('Kg Price')}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={data.kg_price}
                    onChange={(e) => {
                      const kg_price = e.target.value;
                      setData((prev) => {
                        const kgPriceVal = parseFloat(kg_price);
                        const priceVal = parseFloat(prev.price) || 0;
                        const kg_ratio = kgPriceVal && priceVal ? (kgPriceVal / priceVal).toFixed(2) : '';
                        return { ...prev, kg_price, kg_ratio };
                      });
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <NumberField
                    label={tCommon('Profit Margin (%)')}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={data.profit_margin}
                    onChange={(e) => {
                      const profit_margin = e.target.value;
                      setData((prev) => ({ ...prev, profit_margin }));
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    label={tCommon('Product Barcode')}
                    variant="outlined"
                    size="small"
                    fullWidth
                    name="ean"
                    value={data.ean}
                    onChange={(e) => setData({ ...data, ean: e.target.value })}
                    error={Boolean(duplicateBarcodeItem)}
                    helperText={
                      duplicateBarcodeItem
                        ? `Barcode already assigned to: ${duplicateBarcodeItem.name} (PLU: ${duplicateBarcodeItem.plu})`
                        : ''
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="Barcode Scan Supported">
                            <QrCodeScannerIcon style={{ fontSize: '18px', color: duplicateBarcodeItem ? '#d32f2f' : '#2563eb' }} />
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                {!ShopsHelper.isFreePlan(shop) && (
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={tCommon('External Item ID')}
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={data.external_item_id}
                      onChange={(e) => setData({ ...data, external_item_id: e.target.value })}
                    />
                  </Grid>
                )}

                <Grid item xs={12} sm={4}>
                  <NumberField
                    label={tCommon('Display Order')}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={data.order}
                    onChange={(e) => setData({ ...data, order: e.target.value })}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <NumberField
                    label={tCommon('Stock On Hold')}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={data.stock_on_hold}
                    onChange={(e) => setData({ ...data, stock_on_hold: e.target.value })}
                  />
                </Grid>

                <Grid container item xs={12}>
                  <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center', minHeight: '40px' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          checked={data.is_available}
                          onChange={() => setData({ ...data, is_available: !data.is_available })}
                          sx={{ p: 1 }}
                        />
                      }
                      label={tCommon('Available for sale')}
                      sx={{ ml: -1 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center', minHeight: '40px' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          checked={data.is_available_on_cart}
                          onChange={() => setData({ ...data, is_available_on_cart: !data.is_available_on_cart })}
                          sx={{ p: 1 }}
                        />
                      }
                      label={tCommon('Available On Cart')}
                      sx={{ ml: -1 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center', minHeight: '40px' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          checked={data.sync_enabled}
                          onChange={() => setData({ ...data, sync_enabled: !data.sync_enabled })}
                          sx={{ p: 1 }}
                        />
                      }
                      label={tCommon('Export to Scale')}
                      sx={{ ml: -1 }}
                    />
                  </Grid>
                </Grid>

                {ShopsHelper.hasProAccess(shop) &&
                  data.type !== ITEM_TYPES.SERVICE &&
                  data.type !== ITEM_TYPES.COMBO && (
                    <Grid item xs={12}>
                      <div className={styles.batchSection}>
                        <div className={styles.batchHeader}>
                          <span className={styles.batchTitle}>{tCommon('Batch Tracking Enabled')}</span>
                          <Checkbox
                            color="primary"
                            checked={data.is_batch_enabled}
                            onChange={() => setData({ ...data, is_batch_enabled: !data.is_batch_enabled })}
                          />
                        </div>
                        {data.is_batch_enabled && (
                          <div className={styles.batchFields}>
                            <Grid container spacing={1}>
                              <Grid item xs={12} sm={4}>
                                <NumberField
                                  label={
                                    shelfDurationUnit === 'days'
                                      ? tCommon('Shelf Duration (Days)')
                                      : tCommon('Shelf Duration (Hours)')
                                  }
                                  variant="outlined"
                                  size="small"
                                  fullWidth
                                  value={
                                    shelfDurationUnit === 'days'
                                      ? data.shelf_duration_in_hours
                                        ? (Number(data.shelf_duration_in_hours) / 24).toString()
                                        : ''
                                      : data.shelf_duration_in_hours
                                  }
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    const storedVal =
                                      shelfDurationUnit === 'days' ? (val ? (Number(val) * 24).toString() : '') : val;
                                    setData({ ...data, shelf_duration_in_hours: storedVal });
                                  }}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <ToggleButtonGroup
                                          size="small"
                                          value={shelfDurationUnit}
                                          exclusive
                                          onChange={(e, newUnit) => {
                                            if (newUnit) setShelfDurationUnit(newUnit);
                                          }}
                                          sx={{ height: '28px' }}
                                        >
                                          <ToggleButton value="hours" sx={{ px: 1, py: 0, fontSize: '0.7rem' }}>
                                            Hrs
                                          </ToggleButton>
                                          <ToggleButton value="days" sx={{ px: 1, py: 0, fontSize: '0.7rem' }}>
                                            Days
                                          </ToggleButton>
                                        </ToggleButtonGroup>
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <NumberField
                                  label={
                                    expiryAlertUnit === 'days'
                                      ? tCommon('Alert Before Exp. (Days)')
                                      : tCommon('Alert Before Exp. (Hours)')
                                  }
                                  variant="outlined"
                                  size="small"
                                  fullWidth
                                  value={
                                    expiryAlertUnit === 'days'
                                      ? data.expiry_alert_duration_in_hours
                                        ? (Number(data.expiry_alert_duration_in_hours) / 24).toString()
                                        : ''
                                      : data.expiry_alert_duration_in_hours
                                  }
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    const storedVal =
                                      expiryAlertUnit === 'days' ? (val ? (Number(val) * 24).toString() : '') : val;
                                    setData({ ...data, expiry_alert_duration_in_hours: storedVal });
                                  }}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <ToggleButtonGroup
                                          size="small"
                                          value={expiryAlertUnit}
                                          exclusive
                                          onChange={(e, newUnit) => {
                                            if (newUnit) setExpiryAlertUnit(newUnit);
                                          }}
                                          sx={{ height: '28px' }}
                                        >
                                          <ToggleButton value="hours" sx={{ px: 1, py: 0, fontSize: '0.7rem' }}>
                                            Hrs
                                          </ToggleButton>
                                          <ToggleButton value="days" sx={{ px: 1, py: 0, fontSize: '0.7rem' }}>
                                            Days
                                          </ToggleButton>
                                        </ToggleButtonGroup>
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <NumberField
                                  label={
                                    expectedSoldOutUnit === 'days'
                                      ? tCommon('Exp. Sold Out (Days)')
                                      : tCommon('Exp. Sold Out (Hours)')
                                  }
                                  variant="outlined"
                                  size="small"
                                  fullWidth
                                  value={
                                    expectedSoldOutUnit === 'days'
                                      ? data.expected_sold_out_duration_in_hours
                                        ? (Number(data.expected_sold_out_duration_in_hours) / 24).toString()
                                        : ''
                                      : data.expected_sold_out_duration_in_hours
                                  }
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    const storedVal =
                                      expectedSoldOutUnit === 'days' ? (val ? (Number(val) * 24).toString() : '') : val;
                                    setData({ ...data, expected_sold_out_duration_in_hours: storedVal });
                                  }}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <ToggleButtonGroup
                                          size="small"
                                          value={expectedSoldOutUnit}
                                          exclusive
                                          onChange={(e, newUnit) => {
                                            if (newUnit) setExpectedSoldOutUnit(newUnit);
                                          }}
                                          sx={{ height: '28px' }}
                                        >
                                          <ToggleButton value="hours" sx={{ px: 1, py: 0, fontSize: '0.7rem' }}>
                                            Hrs
                                          </ToggleButton>
                                          <ToggleButton value="days" sx={{ px: 1, py: 0, fontSize: '0.7rem' }}>
                                            Days
                                          </ToggleButton>
                                        </ToggleButtonGroup>
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>
                            </Grid>
                          </div>
                        )}
                      </div>
                    </Grid>
                  )}

                <Grid item xs={12} sm={4}>
                  <TextField
                    label={tCommon('Internal Name')}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={data.internal_name}
                    onChange={(e) => setData({ ...data, internal_name: e.target.value })}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    openOnFocus
                    disablePortal
                    PopperProps={{ style: { zIndex: 14000 } }}
                    multiple
                    fullWidth
                    limitTags={2}
                    getLimitTagsText={(more) => `+${more} more`}
                    options={chargeGroups || []}
                    getOptionLabel={(option) => {
                      if (!option) return '';
                      return option.is_global ? `${option.name || ''} (${tCommon('default')})` : option.name || '';
                    }}
                    isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                    ListboxProps={{ style: { maxHeight: '300px', overflow: 'auto' } }}
                    value={(chargeGroups || []).filter((g) => (data.charge_groups || []).map(String).includes(String(g.id)))}
                    onChange={(event, newValue) => {
                      const newChargeGroups = newValue.map((v) => v.id);
                      setData({
                        ...data,
                        charge_groups: newChargeGroups,
                        ...(newChargeGroups.length > 0 ? { tax: 0, cess: 0 } : {}),
                      });
                    }}
                    sx={{
                      '& .MuiAutocomplete-tag': {
                        fontSize: '0.65rem',
                        height: '18px',
                      },
                    }}
                    renderInput={(params) => {
                      const selectedCount = (data.charge_groups || []).length;
                      return (
                        <TextField
                          {...params}
                          variant="outlined"
                          label={tCommon('Charge / Discount Groups')}
                          placeholder={selectedCount < 2 ? tCommon('Select Groups') : ''}
                          size="small"
                        />
                      );
                    }}
                  />
                </Grid>
                {data.type !== ITEM_TYPES.SERVICE && data.type !== ITEM_TYPES.COMBO && (
                  <Grid item xs={12} sm={4}>
                    <NumberField
                      label={tCommon('Low Stock Alert')}
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={data.low_stock_alert_quantity}
                      onChange={(e) => setData({ ...data, low_stock_alert_quantity: e.target.value })}
                    />
                  </Grid>
                )}

                {/* Images */}
                <Grid item xs={12}>
                  <h3 className={styles.inputLabel}>{tCommon('Images')}</h3>
                  <Grid container spacing={1}>
                    {['image', 'image_2', 'image_3', 'image_4'].map((field) => (
                      <Grid item xs={6} sm={3} key={field}>
                        <label className={styles.imageBox} htmlFor={`upload-${field}`}>
                          <CardMedia
                            className={styles.imagePreview}
                            image={imageData[field] || item?.[field] || '/placeholder-image.png'}
                          />
                          <Tooltip title={tPopup('AI Suggestions')}>
                            <IconButton
                              size="small"
                              className={styles.aiSuggestionBtn}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                openAIImageSuggestion(field);
                              }}
                            >
                              <AssistantIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <input
                            id={`upload-${field}`}
                            type="file"
                            hidden
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  setImageData({ ...imageData, [field]: reader.result });
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                          />
                        </label>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>

                {/* Product Options */}
                <Grid item xs={12}>
                  <h3 className={styles.inputLabel}>{tCommon('Product Options')}</h3>
                  <TableContainer component={Paper} className={styles.tableContainer}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: '40%' }}>{tCommon('Label')}</TableCell>
                          <TableCell sx={{ width: '25%' }}>{tCommon('Quantity Ratio')}</TableCell>
                          <TableCell sx={{ width: '25%' }}>{tCommon('Price')}</TableCell>
                          <TableCell sx={{ width: '10%' }}>{tCommon('Actions')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {productOptions.map((option, index) => (
                          <TableRow key={index}>
                            <TableCell>{option.label}</TableCell>
                            <TableCell>{option.quantity_ratio}</TableCell>
                            <TableCell>{option.price || 0}</TableCell>
                            <TableCell>
                              <IconButton size="small" onClick={() => handleEditProdOption(option)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" color="error" onClick={() => handleDltProdOption(option)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell>
                            <TextField
                              size="small"
                              fullWidth
                              label={tCommon('Label')}
                              variant="outlined"
                              value={selectedProdOption?.label || ''}
                              onChange={(e) => setSelectedProdOption({ ...selectedProdOption, label: e.target.value })}
                            />
                          </TableCell>
                          <TableCell>
                            <NumberField
                              size="small"
                              fullWidth
                              label={tCommon('Qty Ratio')}
                              value={selectedProdOption?.quantity_ratio || ''}
                              onChange={(e) =>
                                setSelectedProdOption({ ...selectedProdOption, quantity_ratio: e.target.value })
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <NumberField
                              size="small"
                              fullWidth
                              label={tCommon('Price')}
                              value={selectedProdOption?.price || ''}
                              onChange={(e) =>
                                setSelectedProdOption({ ...selectedProdOption, price: Number(e.target.value) })
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              onClick={() => handleAddProdOption(selectedProdOption)}
                              disabled={!selectedProdOption?.label}
                            >
                              <AddRoundedIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                {/* Price Categories and Addons */}
                <Grid item xs={12} ref={priceCategorySectionRef}>
                  <h3 className={styles.inputLabel}>{tCommon('Price Categories')}</h3>
                  <TableContainer component={Paper} className={styles.tableContainer}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: '40%' }}>{tCommon('Category')}</TableCell>
                          <TableCell sx={{ width: '25%' }}>{tCommon('Qty')}</TableCell>
                          <TableCell sx={{ width: '20%' }}>{tCommon('Price')}</TableCell>
                          <TableCell sx={{ width: '15%' }}>{tCommon('Actions')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {groupedPriceCategoryItems.map((group) => {
                          const { category, items } = group;
                          return items.map((pc, index) => (
                            <TableRow key={pc.id}>
                              {index === 0 && (
                                <TableCell rowSpan={items.length} style={{ fontWeight: 600 }}>
                                  {category?.name}
                                </TableCell>
                              )}
                              <TableCell>
                                {items.length === 1 && pc.minimum_quantity === 0
                                  ? '0'
                                  : index === items.length - 1
                                  ? `${pc.minimum_quantity} +`
                                  : `${pc.minimum_quantity} - ${items[index + 1].minimum_quantity}`}
                              </TableCell>
                              <TableCell>{pc.price}</TableCell>
                              <TableCell>
                                <IconButton size="small" onClick={() => handleEditCategoryItem(pc)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" color="error" onClick={() => handleDltCategoryItem(pc)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ));
                        })}
                        <TableRow>
                          <TableCell>
                            <Autocomplete
                              openOnFocus
                              disablePortal
                              PopperProps={{ style: { zIndex: 14000 } }}
                              size="small"
                              options={priceCategories || []}
                              getOptionLabel={(option) => option?.name || ''}
                              renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                  {option.name}
                                </li>
                              )}
                              isOptionEqualToValue={(option, value) => option?.id === value?.id}
                              filterOptions={(options, { inputValue }) => {
                                const search = (inputValue || '').trim().toLowerCase();
                                if (!search) return options;
                                return options.filter((option) => (option?.name || '').toLowerCase().includes(search));
                              }}
                              value={
                                (priceCategories || []).find(
                                  (c) =>
                                    c.id ===
                                    (selectedPriceCategoryItem?.category_id || selectedPriceCategoryItem?.category?.id)
                                ) || null
                              }
                              onChange={(e, val) =>
                                setSelectedPriceCategoryItem({
                                  ...selectedPriceCategoryItem,
                                  category_id: val?.id,
                                  categoryName: val?.name,
                                })
                              }
                              renderInput={(params) => (
                                <TextField {...params} label={tCommon('Search Category')} variant="outlined" />
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            <NumberField
                              size="small"
                              value={selectedPriceCategoryItem?.minimum_quantity ?? ''}
                              onChange={(e) =>
                                setSelectedPriceCategoryItem({
                                  ...selectedPriceCategoryItem,
                                  minimum_quantity: Number(e.target.value),
                                })
                              }
                              label="Min Qty"
                            />
                          </TableCell>
                          <TableCell>
                            <NumberField
                              size="small"
                              value={selectedPriceCategoryItem?.price || ''}
                              onChange={(e) =>
                                setSelectedPriceCategoryItem({
                                  ...selectedPriceCategoryItem,
                                  price: Number(e.target.value),
                                })
                              }
                              label="Price"
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              onClick={() => handleAddCategoryItem(selectedPriceCategoryItem)}
                              disabled={!selectedPriceCategoryItem?.category_id}
                            >
                              <AddRoundedIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                {/* Variants (Edit only) */}
                {isEdit && (
                  <Grid item xs={12}>
                    <h3 className={styles.inputLabel}>{tCommon('Product Variants')}</h3>
                    {variants.length > 0 ? (
                      <div className={styles.variantSection}>
                        <div className={styles.variantDimensionGroup}>
                          <ToggleButtonGroup
                            color="primary"
                            value={String(selectedDimension)}
                            exclusive
                            onChange={(event, newValue) => {
                              if (newValue !== null) {
                                setSelectedDimension(newValue);
                              }
                            }}
                            size="small"
                            className={styles.typeToggleGroup}
                          >
                            {(productVariantDimensions || []).map((d) => (
                              <ToggleButton key={d.id} value={String(d.id)} className={styles.typeToggleButton}>
                                {d.name}
                              </ToggleButton>
                            ))}
                          </ToggleButtonGroup>
                          <IconButton
                            size="small"
                            className={styles.variantAddBtnSmall}
                            onClick={() =>
                              setVariantMappingData({
                                item: item,
                                baseProduct:
                                  variants[0]?.baseProduct ||
                                  (contextBaseProducts || []).find((bp) => String(bp.id) === String(item?.product)),
                              })
                            }
                          >
                            <AddRoundedIcon fontSize="small" />
                          </IconButton>
                        </div>
                        <div className={styles.variantLabelContainer}>
                          {selectedDimensionLabels.map((label) => (
                            <div key={label.id} className={styles.variantPill}>
                              <span>{label?.name}</span>
                              <div
                                className={styles.variantRemoveBtn}
                                onClick={() =>
                                  setDltProductVariantMapping(
                                    (contextProductVariantMapping || []).find(
                                      (m) => String(m.label) === String(label.id) && String(m.item) === String(item.id)
                                    )
                                  )
                                }
                              >
                                <CloseIcon style={{ fontSize: '14px' }} />
                              </div>
                            </div>
                          ))}
                          <IconButton
                            size="small"
                            className={styles.variantAddBtnSmall}
                            onClick={() =>
                              setVariantMappingData({
                                item: item,
                                baseProduct:
                                  variants[0]?.baseProduct ||
                                  (contextBaseProducts || []).find((bp) => String(bp.id) === String(item?.product)),
                                dimension: (variants || []).find(
                                  (v) => String(v?.dimension?.id) === String(selectedDimension)
                                )?.dimension,
                              })
                            }
                          >
                            <AddRoundedIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </div>
                    ) : (
                      <div
                        className={styles.addDiv}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '10px',
                          backgroundColor: '#f5f5f5',
                          borderRadius: '4px',
                        }}
                      >
                        <span>{tCommon('Add variants for this item')}</span>
                        <Button
                          onClick={() =>
                            setVariantMappingData({
                              item,
                              baseProduct: (contextBaseProducts || []).find(
                                (bp) => String(bp.id) === String(item?.product)
                              ),
                            })
                          }
                          variant="contained"
                          size="small"
                        >
                          {tCommon('Add')}
                        </Button>
                      </div>
                    )}
                  </Grid>
                )}

                <Grid item xs={12} ref={addonSectionRef}>
                  <h3 className={styles.inputLabel}>{tCommon('Addon Items')}</h3>
                  <TableContainer component={Paper} className={styles.tableContainer}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: '40%' }}>{tCommon('Item')}</TableCell>
                          <TableCell sx={{ width: '25%' }}>{tCommon('Price Delta')}</TableCell>
                          <TableCell sx={{ width: '15%' }}>{tCommon('Max Qty')}</TableCell>
                          <TableCell sx={{ width: '10%' }}>{tCommon('Active')}</TableCell>
                          <TableCell sx={{ width: '10%' }}>{tCommon('Actions')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {itemAddons.map((addon, index) => (
                          <TableRow key={addon.id || index}>
                            <TableCell>{addon.itemName}</TableCell>
                            <TableCell>
                              {addon.price_delta > 0
                                ? `${addon.price_delta} more`
                                : addon.price_delta < 0
                                ? `${Math.abs(addon.price_delta)} less`
                                : addon.price_delta}
                            </TableCell>
                            <TableCell>{addon.max_quantity}</TableCell>
                            <TableCell>
                              <Switch
                                size="small"
                                checked={addon.is_available !== false}
                                onChange={(e) => {
                                  const updated = [...itemAddons];
                                  updated[index].is_available = e.target.checked;
                                  setItemAddons(updated);
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton size="small" onClick={() => handleEditAddon(addon)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" color="error" onClick={() => handleDltAddon(addon)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell>
                            <Autocomplete
                              openOnFocus
                              disablePortal
                              PopperProps={{ style: { zIndex: 14000 } }}
                              size="small"
                              options={items || []}
                              getOptionLabel={(option) =>
                                option?.plu ? `${option.plu} - ${option.name || ''}` : option?.name || ''
                              }
                              renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                  <Box style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                    <span>{option.name}</span>
                                    <span style={{ color: '#718096', fontSize: '12px' }}>(PLU: {option.plu})</span>
                                  </Box>
                                </li>
                              )}
                              isOptionEqualToValue={(option, value) => option?.id === value?.id}
                              filterOptions={(options, { inputValue }) => {
                                const search = (inputValue || '').trim().toLowerCase();
                                if (!search) return options;
                                return options.filter((option) => {
                                  const name = (option?.name || '').toLowerCase();
                                  const plu = (option?.plu || '').toString().toLowerCase();
                                  const ean = (option?.ean || '').toString().toLowerCase();
                                  return name.includes(search) || plu.includes(search) || ean.includes(search);
                                });
                              }}
                              value={(items || []).find((i) => i.id === selectedAddon?.addon_item_id) || null}
                              onChange={(e, val) =>
                                setSelectedAddon({
                                  ...selectedAddon,
                                  addon_item_id: val?.id,
                                  name: val?.name,
                                  itemName: val?.name,
                                })
                              }
                              renderInput={(params) => (
                                <TextField {...params} label={tCommon('Search Item')} variant="outlined" />
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <NumberField
                                size="small"
                                value={selectedAddon?.price_delta || ''}
                                onChange={(e) =>
                                  setSelectedAddon({ ...selectedAddon, price_delta: Number(e.target.value) })
                                }
                                label="Delta"
                              />
                              <ToggleButtonGroup
                                value={addonDeltaType}
                                exclusive
                                onChange={(e, v) => v && setAddonDeltaType(v)}
                                size="small"
                              >
                                <ToggleButton value={DELTA_TYPE.LESS} style={{ padding: '0 15px' }}>
                                  -
                                </ToggleButton>
                                <ToggleButton value={DELTA_TYPE.MORE} style={{ padding: '0 15px' }}>
                                  +
                                </ToggleButton>
                              </ToggleButtonGroup>
                            </div>
                          </TableCell>
                          <TableCell>
                            <NumberField
                              size="small"
                              value={selectedAddon?.max_quantity || ''}
                              onChange={(e) =>
                                setSelectedAddon({ ...selectedAddon, max_quantity: Number(e.target.value) })
                              }
                              label="Max"
                            />
                          </TableCell>
                          <TableCell>
                            <Switch
                              size="small"
                              checked={selectedAddon?.is_available !== false}
                              onChange={(e) => setSelectedAddon({ ...selectedAddon, is_available: e.target.checked })}
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              onClick={() => handleAddAddon(selectedAddon)}
                              disabled={!selectedAddon?.addon_item_id}
                            >
                              <AddRoundedIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                {/* Tags */}
                <Grid item xs={12}>
                  <h3 className={styles.inputLabel}>{tCommon('Tags')}</h3>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableBody>
                        {tags.map((tag, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              <Autocomplete
                                freeSolo
                                size="small"
                                options={PRODUCTS_TAG_SUGGESTIONS?.map((s) => s.key) || []}
                                value={tag.key}
                                onInputChange={(e, val) => {
                                  const updated = [...tags];
                                  updated[idx].key = val;
                                  setTags(updated);
                                }}
                                renderInput={(params) => <TextField {...params} placeholder="Key" variant="outlined" />}
                              />
                            </TableCell>
                            <TableCell>
                              <Autocomplete
                                freeSolo
                                size="small"
                                options={PRODUCTS_TAG_SUGGESTIONS?.find((s) => s.key === tag.key)?.values || []}
                                value={tag.value}
                                onInputChange={(e, val) => {
                                  const updated = [...tags];
                                  updated[idx].value = val;
                                  setTags(updated);
                                }}
                                renderInput={(params) => (
                                  <TextField {...params} placeholder="Value" variant="outlined" />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton onClick={() => setTags(tags.filter((_, i) => i !== idx))}>
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell colSpan={3}>
                            <Button
                              startIcon={<AddCircleIcon />}
                              onClick={() => setTags([...tags, { key: '', value: '' }])}
                            >
                              Add Tag
                            </Button>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                {/* Schedules */}
                {ShopsHelper.hasProAccess(shop) && (
                  <Grid item xs={12}>
                    <h3 className={styles.inputLabel}>{tCommon('Product Schedule')}</h3>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <h4 style={{ margin: '10px 0 5px 0', fontSize: '14px', color: '#555' }}>
                          {tCommon('Available Days')}
                        </h4>
                        <ToggleButtonGroup
                          value={selectedDays}
                          onChange={handleDayChange}
                          aria-label="days of the week"
                          size="small"
                          fullWidth
                        >
                          {Object.values(DAYS_OF_WEEK).map((day) => (
                            <ToggleButton
                              key={day}
                              value={day}
                              aria-label={day}
                              style={{
                                padding: '6px',
                                color: selectedDays.includes(day) ? '#fff' : '#333',
                                backgroundColor: selectedDays.includes(day) ? '#0b8300a8' : '#ffffff00',
                                border: '1px solid #ccc',
                                borderRadius: 3,
                                fontWeight: 500,
                              }}
                            >
                              {day}
                            </ToggleButton>
                          ))}
                        </ToggleButtonGroup>
                      </Grid>

                      {timeRanges.map((tr, idx) => (
                        <Grid item xs={12} key={idx}>
                          <Grid container spacing={1} alignItems="center">
                            <Grid item xs={5}>
                              <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <TimePicker
                                  label={tCommon('Start Time')}
                                  value={tr.from}
                                  onChange={(newVal) => handleTimeRangeChange(idx, 'from', newVal)}
                                  format="hh:mma"
                                  slotProps={{
                                    textField: {
                                      size: 'small',
                                      fullWidth: true,
                                      InputProps: {
                                        sx: { fontSize: '0.875rem', alignItems: 'center' },
                                      },
                                    },
                                  }}
                                />
                              </LocalizationProvider>
                            </Grid>

                            <Grid item xs={5}>
                              <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <TimePicker
                                  label={tCommon('End Time')}
                                  value={tr.to}
                                  onChange={(newVal) => handleTimeRangeChange(idx, 'to', newVal)}
                                  format="hh:mma"
                                  slotProps={{
                                    textField: {
                                      size: 'small',
                                      fullWidth: true,
                                      InputProps: {
                                        sx: { fontSize: '0.875rem', alignItems: 'center' },
                                      },
                                    },
                                  }}
                                />
                              </LocalizationProvider>
                            </Grid>

                            <Grid item xs={2}>
                              <IconButton
                                size="small"
                                onClick={() => removeRangeRow(idx)}
                                disabled={timeRanges.length === 1}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Grid>
                          </Grid>
                        </Grid>
                      ))}

                      <Grid item xs={12}>
                        <Button size="small" variant="outlined" startIcon={<LibraryAddIcon />} onClick={addRangeRow}>
                          <span style={{ paddingTop: 5 }}>{tCommon('Add New Time Schedule')}</span>
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <h3 className={styles.inputLabel}>{tCommon('Other Names')}</h3>
                  {otherNames.map((item, index) => (
                    <Grid container spacing={1} key={index} alignItems="center" className={styles.otherNameRow}>
                      <Grid item xs={6} sm={8}>
                        <TextField
                          label={tCommon('Name')}
                          variant="outlined"
                          size="small"
                          value={item.name}
                          onChange={(e) => handleOtherNameChange(index, 'name', e.target.value)}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={5} sm={3}>
                        <TextField
                          label={`${tCommon('Option')} (${tCommon('Optional')})`}
                          variant="outlined"
                          size="small"
                          value={item.option}
                          onChange={(e) => handleOtherNameChange(index, 'option', e.target.value)}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={1} sm={1}>
                        <IconButton onClick={() => handleRemoveOtherNameRow(index)} size="small" color="secondary">
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}
                  <Button
                    startIcon={<AddCircleIcon />}
                    onClick={handleAddOtherNameRow}
                    size="small"
                    variant="outlined"
                    color="primary"
                    sx={{ mt: 1 }}
                  >
                    {tCommon('Add Other Name')}
                  </Button>
                </Grid>

                <Grid item xs={12}>
                  <DescriptionField
                    value={data.description}
                    onChange={(val) => setData({ ...data, description: val })}
                  />
                </Grid>

                {/* Advanced Info */}
                <Grid item xs={12}>
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>{tCommon('Advanced Details')}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={1}>
                        {[1, 2, 3, 4, 5, 6, 7].map((num) => (
                          <Grid item xs={12} sm={6} key={num}>
                            <TextField
                              label={tCommon(`Text ${num}`)}
                              variant="outlined"
                              size="small"
                              fullWidth
                              value={itemAdvanced[`text${num}`] || ''}
                              onChange={(e) => setItemAdvanced({ ...itemAdvanced, [`text${num}`]: e.target.value })}
                            />
                          </Grid>
                        ))}
                        <Grid item xs={12} sm={6}>
                          <NumberField
                            label={tCommon('Min Qty')}
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={itemAdvanced.min_quantity || ''}
                            onChange={(e) => setItemAdvanced({ ...itemAdvanced, min_quantity: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <NumberField
                            label={tCommon('Max Qty')}
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={itemAdvanced.max_quantity || ''}
                            onChange={(e) => setItemAdvanced({ ...itemAdvanced, max_quantity: e.target.value })}
                          />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              </>
            )}
          </Grid>
        </div>

        <div className={styles.footSec}>
          <DialogActions>
            <Button className={styles.oldCloseBtn} onClick={handleClose}>
              {tCommon('Close')}
            </Button>
            <Button
              className={styles.oldSaveBtn}
              onClick={onSaveItemButtonClicked}
              disabled={
                isLoading ||
                data.name === '' ||
                data.category === '' ||
                data.price === '' ||
                data.product_type === '' ||
                data.unit === ''
              }
            >
              {isLoading ? (
                <CircularProgress size={20} sx={{ color: 'white' }} />
              ) : isEdit ? (
                'Update Item'
              ) : (
                'Create Item'
              )}
            </Button>
          </DialogActions>
        </div>
      </div>

      {isShowSelectShopsDialog && (
        <SelectShopsDialog
          onClose={() => setIsShowSelectShopsDialog(false)}
          onShopsSelected={onOtherShopsSelected}
          message={tPopup("Do you want to update item with PLU '{{plu}}' in all shops?", { plu: data.plu })}
        />
      )}
      {isShowAddCategoryDialog && (
        <AddCategoryDialog handleClose={() => setIsShowAddCategoryDialog(false)} onSuccess={onCreateCategory} />
      )}
      {variantMappingData && (
        <CreateVariantMappingDialog
          onClose={() => setVariantMappingData(null)}
          onSuccess={() => fetchVariantMapping(true)}
          data={variantMappingData}
        />
      )}
      <ConfirmPopup
        data={dltProductVariantMapping}
        handleClose={() => setDltProductVariantMapping(null)}
        handleOkay={handleDeleteProductVariantMapping}
        alertTitle={tPopup('Confirm delete')}
        successBtnName={tCommon('Delete')}
        alertContent={tPopup("Deleted variant can't be restored, Are you sure to delete this variant?")}
      />

      <AIImageSuggestionDialog
        open={isAIImageSuggestionDialogOpen}
        handleClose={() => setIsAIImageSuggestionDialogOpen(false)}
        onSelect={handleAIImageSelect}
        suggestionKey={data.name}
      />
    </div>
  );
}
